import { useState, useEffect } from 'react';
import BasicModal from './BasicModal';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import {
  createUserPacketTemplate,
  updateUserPacketTemplate,
} from '../../services/UserPacketTemplateService';
import { Mixpanel } from '../../services/Mixpanel';

export const AddToTemplatesModal = ({
  showModal,
  setShowModal,
  packetId,
  packetTitle,
  templateToEdit = null,
  onTemplateUpdated = null,
  source = 'unknown',
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!templateToEdit;

  // Track when modal opens
  useEffect(() => {
    if (showModal) {
      if (isEditMode) {
        Mixpanel.track('Edit Template Modal Opened', {
          template_id: templateToEdit.id,
          template_title: templateToEdit.title,
          source,
        });
      } else if (packetId) {
        Mixpanel.track('Add To Templates Modal Opened', {
          packet_id: packetId,
          packet_title: packetTitle,
          source,
        });
      }
    }
  }, [showModal, packetId, packetTitle, isEditMode, templateToEdit, source]);

  // Initialize/reset state when modal opens or closes
  useEffect(() => {
    if (showModal && isEditMode) {
      setTitle(templateToEdit.title || '');
      setDescription(templateToEdit.description || '');
      setError(null);
      setSuccess(false);
    } else if (!showModal) {
      setTitle('');
      setDescription('');
      setError(null);
      setSuccess(false);
    }
  }, [showModal, isEditMode, templateToEdit]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Please enter a title for your template.');
      return;
    }

    setIsSaving(true);
    setError(null);

    if (isEditMode) {
      Mixpanel.track('Edit Template Save Clicked', {
        template_id: templateToEdit.id,
        template_title: title,
        has_description: !!description.trim(),
        source,
      });

      try {
        const response = await updateUserPacketTemplate(templateToEdit.id, {
          title: title.trim(),
          description: description.trim(),
        });

        Mixpanel.track('Edit Template Success', {
          template_id: templateToEdit.id,
          template_title: title,
          source,
        });

        setSuccess(true);

        // Notify parent of the updated template
        if (onTemplateUpdated) {
          onTemplateUpdated(response.data.results);
        }

        // Close modal after showing success
        setTimeout(() => {
          setShowModal(false);
        }, 2000);
      } catch (err) {
        Mixpanel.track('Edit Template Failed', {
          template_id: templateToEdit.id,
          error: err.message,
          source,
        });
        setError(err.message || 'Failed to update template. Please try again.');
      } finally {
        setIsSaving(false);
      }
    } else {
      Mixpanel.track('Add To Templates Save Clicked', {
        packet_id: packetId,
        packet_title: packetTitle,
        template_title: title,
        has_description: !!description.trim(),
        source,
      });

      try {
        await createUserPacketTemplate(title.trim(), packetId, description.trim());

        Mixpanel.track('Add To Templates Success', {
          packet_id: packetId,
          packet_title: packetTitle,
          template_title: title,
          source,
        });

        setSuccess(true);

        // Close modal after showing success
        setTimeout(() => {
          setShowModal(false);
        }, 4000);
      } catch (err) {
        Mixpanel.track('Add To Templates Failed', {
          packet_id: packetId,
          packet_title: packetTitle,
          error: err.message,
          source,
        });
        setError(err.message || 'Failed to save template. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      title={success ? undefined : isEditMode ? 'Edit Template' : 'Add to Templates'}
      maxWidth='sm:max-w-lg'>
      <div className='mt-4'>
        {success ? (
          <div className='text-center py-4'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 mb-4'>
              <Squares2X2Icon className='h-6 w-6 text-emerald-600' />
            </div>
            <p className='text-lg font-semibold text-gray-900'>
              {isEditMode ? 'Template Updated!' : 'Template Saved!'}
            </p>
            <p className='text-sm text-gray-600 mt-2'>
              {isEditMode
                ? 'Your template has been updated successfully.'
                : 'You can find it in your templates when creating new resources.'}
            </p>
          </div>
        ) : (
          <>
            <p className='text-sm text-gray-600 mb-4'>
              {isEditMode
                ? 'Update the title and description for this template.'
                : 'Save this resource as a template to create new resources with similar structure but different contents.'}
            </p>

            <div className='space-y-4'>
              <div>
                <label
                  htmlFor='template-title'
                  className='block text-sm font-medium text-gray-700 mb-1'>
                  Title <span className='text-red-500'>*</span>
                </label>
                <input
                  id='template-title'
                  type='text'
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setError(null);
                  }}
                  placeholder='e.g., Reading Comprehension Activity'
                  className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                />
              </div>

              <div>
                <label
                  htmlFor='template-description'
                  className='block text-sm font-medium text-gray-700 mb-1'>
                  Description <span className='text-gray-400'>(optional)</span>
                </label>
                <p className='text-xs text-gray-500 mb-2'>
                  This will help Diffit create the resources you need more accurately.
                </p>
                <textarea
                  id='template-description'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='This template is used to...'
                  rows={4}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 resize-none'
                />
              </div>
            </div>

            {error && (
              <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-md'>
                <p className='text-sm text-red-800'>{error}</p>
              </div>
            )}

            <div className='mt-6 flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => {
                  if (isEditMode) {
                    Mixpanel.track('Edit Template Cancelled', {
                      template_id: templateToEdit.id,
                      template_title: templateToEdit.title,
                      source,
                    });
                  } else {
                    Mixpanel.track('Add To Templates Cancelled', {
                      packet_id: packetId,
                      packet_title: packetTitle,
                      had_title: !!title.trim(),
                      had_description: !!description.trim(),
                      source,
                    });
                  }
                  setShowModal(false);
                }}
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'>
                Cancel
              </button>
              <button
                type='button'
                onClick={handleSave}
                disabled={isSaving}
                className='px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'>
                {isSaving ? 'Saving...' : isEditMode ? 'Update Template' : 'Save Template'}
              </button>
            </div>
          </>
        )}
      </div>
    </BasicModal>
  );
};
